import { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { getUserLocalLogin, useCurrentUser } from "@/lib/auth";
import type { AiResolutionSuggestion, SupportTag, SupportTicket } from "@/lib/support";
import { listAiResolutionSuggestions, listSupportTickets } from "@/lib/support";
import { useRealtime } from "@/lib/useRealtime";
import { AdminView } from "@/pages/support/AdminView";
import {
  NewTicketDialog,
  TicketHistoryDialog,
} from "@/pages/support/dialogs";
import { UserView } from "@/pages/support/UserView";
import { sortUserTickets } from "@/pages/support/utils";
import "@/pages/support/supportPage.css";

function userIsAppAdmin(groups: string[] | undefined): boolean {
  return (groups || []).some((g) => /app-admins/i.test(g));
}

export function SupportIndexPage() {
  const { user } = useCurrentUser();
  const currentLogin = getUserLocalLogin(user).toLowerCase();
  const groups = user?.groups as string[] | undefined;

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [tags, setTags] = useState<SupportTag[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<AiResolutionSuggestion[]>([]);
  const [newOpen, setNewOpen] = useState(false);
  const [historyTicket, setHistoryTicket] = useState<SupportTicket | null>(null);

  const loadTickets = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const fetchOnlyOpen = userIsAppAdmin(groups) ? onlyOpen : false;
      const data = await listSupportTickets(fetchOnlyOpen);
      const fallbackAdmin = userIsAppAdmin(groups);
      setIsAdmin(typeof data.admin === "boolean" ? data.admin : fallbackAdmin);
      setTickets(data.items || []);
      setTags(data.tags || []);
      if (typeof data.admin === "boolean" ? data.admin : fallbackAdmin) {
        try {
          const ai = await listAiResolutionSuggestions(0.6);
          setAiSuggestions(ai.items || []);
        } catch {
          setAiSuggestions([]);
        }
      } else {
        setAiSuggestions([]);
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Tickets konnten nicht geladen werden.";
      setError(message);
      if (!options?.silent) setTickets([]);
    } finally {
      setLoading(false);
    }
  }, [onlyOpen, groups]);

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  // Realtime-Änderungen still einpflegen – die Liste bleibt dabei stehen.
  useRealtime({
    scopes: ["domain:support", "support:list"],
    domains: ["support"],
    events: [
      "support_ticket_created",
      "support_ticket_status_changed",
      "support_ticket_reply_created",
      "support_changed",
    ],
    onChange: () => {
      void loadTickets({ silent: true });
    },
  });

  const showAdmin = useMemo(() => isAdmin, [isAdmin]);

  const userTickets = useMemo(() => sortUserTickets(tickets), [tickets]);

  return (
    <AppShell title="Support">
      {error ? (
        <Alert variant="error" title="Tickets konnten nicht geladen werden" className="mb-4">
          {error}
        </Alert>
      ) : null}

      {loading && tickets.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-label-secondary">
          <Spinner />
          <span className="ml-2 text-sm">Tickets werden geladen …</span>
        </div>
      ) : showAdmin ? (
        <AdminView
          tickets={tickets}
          tags={tags}
          aiSuggestions={aiSuggestions}
          onlyOpen={onlyOpen}
          onOnlyOpenChange={setOnlyOpen}
          onRefresh={() => void loadTickets()}
          onNewTicket={() => setNewOpen(true)}
          onOpenHistory={setHistoryTicket}
          currentLogin={currentLogin}
        />
      ) : (
        <UserView
          tickets={userTickets}
          loading={loading}
          onNewTicket={() => setNewOpen(true)}
          onRefresh={() => void loadTickets()}
        />
      )}

      <NewTicketDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={() => void loadTickets()}
      />

      <TicketHistoryDialog
        open={!!historyTicket && showAdmin}
        ticket={historyTicket}
        isAdmin={showAdmin}
        onClose={() => setHistoryTicket(null)}
        onSaved={() => void loadTickets()}
      />
    </AppShell>
  );
}
